import styled from "styled-components";
import {getAspectRatioFromImage, getCssSizeValue, getHoverClass, isStringEmpty} from "@/utils/utils";
import Image from "@/components/common/Image";
import {ICONS, LOCALES} from "@/constants";
import {useRouter} from "next/router";
import Text from "@/components/common/Text";
import Icon from "@/components/common/Icon/Icon";
import {BREAKPOINTS, colors} from "@/styles/themeSettings";
import ExternalLink, {NEW_CARD_VARIANT} from "@/components/common/Links/ExternalLink";
import useHover from "@/hooks/useHover";

const StyledWrapper = styled(ExternalLink)`
    transition: all var(--rgr-duration) var(--ease);
    background: ${props => props.theme.colors.whiteSmoke};
	padding-top: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
	padding-bottom: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: unset;
        padding-top: ${getCssSizeValue(24)};
        padding-bottom: ${getCssSizeValue(38)};
	}
	
	.image-wrapper {
		overflow: hidden;
	}
	
	&.hover {
        background: ${props => props.theme.colors.periwinkle};
	}
`

const StyledDateAndIconWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
    padding: 0 ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
    margin-bottom: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
	
    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: 0 ${getCssSizeValue(24)};
		margin-bottom: ${getCssSizeValue(24)};
	}
	
	.icon {
		width: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};

        @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
            width: ${getCssSizeValue(24)};
		}
	}
`

const StyledText = styled.div`
	margin-top: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
    padding: 0 ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};

    @media (min-width: ${({theme}) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
        margin-top: ${getCssSizeValue(24)};
        padding: 0 ${getCssSizeValue(24)};
	}
`

const StyledImage = styled(Image)`
    transition: all var(--rgr-duration) var(--ease);
    aspect-ratio: ${({$aspectRatio}) => $aspectRatio || '16/9'};
    width: 100%;
    height: auto;
	
    @media (min-width: ${({theme}) => theme.breakpoints.desktop || 1024}px) and (orientation: landscape) {
        width: 100%;
    }

    &.hover {
        transform: scale(1.1);
    }
`

const daysAgo = (dateString) => {
	const postDate = new Date(dateString);
	const now = new Date();
	const timeDifference = now - postDate;
	const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
	return daysDifference;
};

const TRANSLATIONS = {
	[LOCALES.PL]: {
		days_ago: 'dni temu',
		day_ago: 'dzień temu',
		today: 'Dzisiaj'
	},
	[LOCALES.EN]: {
		days_ago: 'days ago',
		day_ago: 'day ago',
		today: 'Today'
	},
	[LOCALES.DE]: {
		days_ago: 'vor Tagen',
		day_ago: 'vor einem Tag',
		today: 'Heute'
	}
}

const CHARACTERS_LIMITS = {
	image: 185,
	no_image: 374
}

function getTranslatedDaysText(locale, created_time) {
	const daysAgoAmount = daysAgo(created_time)
	const translationsWithLocale = TRANSLATIONS[locale]
	return daysAgoAmount === 0
		? translationsWithLocale.today
		: daysAgoAmount === 1
			? `${daysAgoAmount} ${translationsWithLocale.day_ago}`
			: `${daysAgoAmount} ${translationsWithLocale.days_ago}`
}

function getCutMessage(message, image) {
	if (!image) {
		return message.length < CHARACTERS_LIMITS.no_image ? message : message.substring(0, CHARACTERS_LIMITS.no_image) + '...'
	}

	return message.length < CHARACTERS_LIMITS.image ? message : message.substring(0, CHARACTERS_LIMITS.image) + '...'
}

export default function SingleTile({item}) {
	const [ref, hovering] = useHover()
	const permaLinkUrl = item?.permalink_url || null
	const router = useRouter()
	const image = item?.attachments?.data[0]?.media?.image || null
	const message = item?.message || ''
	const translatedDaysText = getTranslatedDaysText(router.locale, item.created_time)
	const cutMessage = getCutMessage(message, image)

	return (
		<StyledWrapper
			className={`typo-borna col-desktop-4 col-tablet-8 col-mobile-8 ${getHoverClass(hovering)}`}
			href={permaLinkUrl}
			newCard={NEW_CARD_VARIANT.YES}
			ref={ref}
		>
			<StyledDateAndIconWrapper>
				<Text className='square static typo-strawford typo-strawford-12'>{translatedDaysText}</Text>
				<div>
					<Icon name={ICONS.FACEBOOK} color={colors.richBlue} />
				</div>
			</StyledDateAndIconWrapper>

			{image && (
				<div className='image-wrapper'>
					<StyledImage
						src={image?.src || ''}
						alt={image?.alt || ''}
						sizes={'100vw'}
						$aspectRatio={getAspectRatioFromImage(image)}
						className={getHoverClass(hovering)}
					/>
				</div>
			)}

			{!isStringEmpty(cutMessage) && (
				<StyledText className='typo-strawford typo-strawford-11'>{cutMessage}</StyledText>
			)}
		</StyledWrapper>
	)
}